import React, {useState} from "react";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {ShapperButton} from "../ShapperButton/ShapperButton";
import {isPushConfigured, isPushSupported, requestPushPermission} from "../../pushNotification";

const DISMISS_KEY = "push_optin_dismissed";

let alreadyLogged = false;

// Filet de sécurité multi-tenant : un thème peut ne pas définir de couleur de texte, ou en
// définir une identique au fond. On retombe alors sur du noir ou du blanc selon la luminance.
const readableTextColor = (background) => {
    const parsed = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(background).trim());

    if (!parsed) {
        return "#000000";
    }

    const hex = parsed[1].length === 3
        ? parsed[1].split("").map((c) => c + c).join("")
        : parsed[1];

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? "#000000" : "#ffffff";
};

// La demande de permission doit partir d'un geste utilisateur : Safari l'exige et les autres
// navigateurs bloquent définitivement le domaine après un refus, d'où le passage par ce bandeau.
export const PushOptIn = () => {

    const {t} = useTranslation();
    const style = useSelector(state => state.style);
    const webParams = useSelector(state => state.application.getApp.webParams);
    const [hidden, setHidden] = useState(() => window.localStorage.getItem(DISMISS_KEY) === "1");

    // "granted" : le token est déjà enregistré à l'init. "denied" : plus rien à proposer.
    const blockedBy =
        hidden ? "déjà écarté par l'utilisateur (localStorage " + DISMISS_KEY + ")"
        : !webParams ? "webParams absent"
        : !window.isSecureContext ? "contexte non sécurisé (https ou localhost requis)"
        : !isPushSupported() ? "navigateur sans serviceWorker/PushManager/Notification"
        : !isPushConfigured(webParams) ? "firebase_project_id ou firebase_v_api_key manquant dans webParams"
        : window.Notification.permission !== "default" ? "permission déjà " + window.Notification.permission
        : null;

    if (blockedBy) {
        // console.warn et non console.log : ce dernier est neutralisé dans les builds de
        // production, or c'est justement là qu'on a besoin de savoir pourquoi rien ne s'affiche.
        if (!alreadyLogged) {
            alreadyLogged = true;
            console.warn("PushOptIn masqué :", blockedBy);
        }
        return null;
    }

    const dismiss = () => {
        window.localStorage.setItem(DISMISS_KEY, "1");
        setHidden(true);
    };

    const accept = () => {
        setHidden(true);
        requestPushPermission(webParams);
    };

    // Attention : pour les composants non-"button", createStyle() dérive "color" du même
    // bg_color_id que "backgroundColor" — c'est donc la couleur de fond, pas celle du texte.
    // La couleur de texte du thème est "text_color", comme dans NavBar.
    const surface = (style && style.cell) || {};
    const backgroundColor = surface.backgroundColor || "#ffffff";
    const themeTextColor = surface.text_color;
    const textColor = (typeof themeTextColor === "string" && themeTextColor.toLowerCase() !== backgroundColor.toLowerCase())
        ? themeTextColor
        : readableTextColor(backgroundColor);

    const barStyle = {
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1300,
        padding: "12px 16px",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: "12px",
        boxShadow: "0 -2px 8px rgba(0,0,0,0.15)",
        backgroundColor: backgroundColor,
        color: textColor,
    };

    return (
        <div style={barStyle}>
            <span style={{flex: "1 1 240px", minWidth: 0}}>{t("g_push_optin_message")}</span>
            <ShapperButton onClick={accept}>{t("g_btn_push_enable")}</ShapperButton>
            <ShapperButton variant="outlined" onClick={dismiss}>{t("g_btn_push_later")}</ShapperButton>
        </div>
    );
}